import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, Button, CircularProgress } from "@material-ui/core";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";
import FolderOpenRoundedIcon from "@material-ui/icons/FolderOpenRounded";
import InsertDriveFileRoundedIcon from "@material-ui/icons/InsertDriveFileRounded";
import { getReportInputUrlRequest } from "../../../PatientApiActions";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "bmp", "heic", "heif"];

// The extension of the stored file, from the url path (the query string carries
// the signature, so strip it first).
const extensionOf = (url, fileName) => {
  const name = fileName || String(url || "").split("?")[0].split("/").pop() || "";
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1).toLowerCase() : "";
};

// What the patient uploaded can be a PDF or a photo of a report, so pick the
// renderer from the served content type, falling back to the file extension.
const kindOf = (contentType, url, fileName) => {
  const type = String(contentType || "").toLowerCase();
  const ext = extensionOf(url, fileName);
  if (type.startsWith("image/") || IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  return "other";
};

// Embeds the document a report was generated FROM — for the blood report, the
// laboratory report the patient uploaded. A report with no stored input is a
// normal state (nothing was uploaded), so it falls back to `fallback` when one
// is given, and to a plain empty state otherwise.
const ReportInputViewer = ({
  phone,
  collection,
  emptyTitle = "No input document",
  emptyNote = "No source document was uploaded for this report.",
  fallback = null,
}) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("loading"); // loading | ready | empty | error
  const [input, setInput] = useState(null);

  useEffect(() => {
    if (!phone || !collection) {
      setStatus("empty");
      return undefined;
    }

    let active = true;
    setStatus("loading");
    dispatch(getReportInputUrlRequest(collection, phone))
      .then((res) => {
        if (!active) return;
        if (res && res.available && res.url) {
          setInput(res);
          setStatus("ready");
        } else {
          setStatus("empty");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });

    return () => {
      active = false;
    };
  }, [dispatch, collection, phone]);

  if (status === "loading") {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "ready" && input) {
    const kind = kindOf(input.contentType, input.url, input.fileName);
    const openInNewTab = () => window.open(input.url, "_blank", "noopener");
    return (
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={openInNewTab}
          >
            Open in new tab
          </Button>
        </Box>
        {kind === "image" && (
          <Box textAlign="center">
            <img
              src={input.url}
              alt={input.fileName || "Uploaded report"}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
        {kind === "pdf" && (
          <iframe
            title="report-input-document"
            src={input.url}
            style={{ width: "100%", height: "70vh", border: "none" }}
          />
        )}
        {/* Anything the browser will not reliably render inline (e.g. a .doc
            scan) — offer it rather than embedding a blank frame. */}
        {kind === "other" && (
          <Box textAlign="center" py={5}>
            <InsertDriveFileRoundedIcon
              style={{ fontSize: 48, color: "#bdbdbd" }}
            />
            <Typography style={{ marginTop: 8, fontWeight: 600 }}>
              {input.fileName || "Uploaded document"}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 4 }}
            >
              This file type cannot be shown here.
            </Typography>
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<OpenInNewRoundedIcon />}
                onClick={openInNewTab}
              >
                Open document
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (status === "empty" && fallback) return fallback;

  return (
    <Box textAlign="center" py={5}>
      <FolderOpenRoundedIcon style={{ fontSize: 48, color: "#bdbdbd" }} />
      <Typography style={{ marginTop: 8, fontWeight: 600 }}>
        {status === "error" ? "Couldn't load document" : emptyTitle}
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
        {status === "error"
          ? "There was a problem fetching this document. Please try again."
          : emptyNote}
      </Typography>
    </Box>
  );
};

export default ReportInputViewer;
